/**
 * Editor-wide context that gets auto-injected into every `EditorTrack.*`
 * event payload — so business call sites can stay clean ("user clicked
 * undo") while dashboards can slice every event by project / workspace /
 * editor session.
 *
 * This is a **process-singleton** living in the browser tab. Set it once
 * when the editor mounts a project (`setEditorProject`), update individual
 * fields as the user navigates (`setEditorCurrentPage`, etc), and clear it
 * when the project unmounts.
 *
 * The values flow into Amplitude in two complementary ways:
 *   - **Event properties**: spread into every event sent via `EditorTrack.*`
 *     so you can `WHERE project_id = ...` in any dashboard chart.
 *   - **Group analytics**: paired with `setGroup('project', projectId)` so
 *     project-level cohorts (e.g. plan_tier, visibility) work end-to-end.
 *
 * Never call this from outside the editor — landing-page / poster apps don't
 * have a project context.
 */

export type ProjectVisibility = 'private' | 'unlisted' | 'public' | 'team';

export interface EditorContext {
  /** Persistent project identifier (Sanity / DB id). */
  project_id?: string;
  /** Origin template id, if the project was created from a template. */
  template_id?: string;
  /** Project visibility, drives "share" funnel analysis. */
  project_visibility?: ProjectVisibility;
  /** Page id currently being viewed/edited on canvas. */
  current_page_id?: string;
  /** Total number of pages in this project. */
  page_count?: number;
  /** Number of nodes in current page (bucketed before send). */
  page_node_count?: number;
  /** Stable per-editor-mount session id (resets on page reload). */
  editor_session_id?: string;
  /** When the editor session started (ISO 8601). */
  editor_session_started_at?: string;
  /** Plan tier of the owner, mirrored from user property for fast filtering. */
  plan_tier?: string;
  /** Workspace / team id, if applicable. */
  workspace_id?: string;
}

let context: EditorContext = {};

/**
 * Replace the editor context wholesale (call once on project mount, again on
 * project switch).
 */
export function setEditorContext(next: EditorContext) {
  context = { ...next };
}

/** Patch individual fields without resetting the rest. */
export function patchEditorContext(patch: Partial<EditorContext>) {
  context = { ...context, ...patch };
}

/** Returns a frozen snapshot of the current context. */
export function getEditorContext(): Readonly<EditorContext> {
  return context;
}

/** Clear all context (call on project unmount / route leave). */
export function clearEditorContext() {
  context = {};
}

/**
 * Project a numeric value to a coarse bucket label so we don't blow up
 * cardinality. Used for high-cardinality counts like current-page node count.
 *
 * Buckets:  0 / 1 / 2-5 / 6-10 / 11-25 / 26-50 / 51-100 / 100+
 */
export function bucketCount(n: number | undefined): string | undefined {
  if (n == null || !Number.isFinite(n)) return undefined;
  if (n <= 0) return '0';
  if (n === 1) return '1';
  if (n <= 5) return '2-5';
  if (n <= 10) return '6-10';
  if (n <= 25) return '11-25';
  if (n <= 50) return '26-50';
  if (n <= 100) return '51-100';
  return '100+';
}

/**
 * Generate a session id (RFC4122-ish without runtime deps). Suitable for
 * `editor_session_id`. Not cryptographically strong; that's not needed here.
 */
export function newEditorSessionId(): string {
  const rand = () => Math.random().toString(36).slice(2, 10);
  return `es_${Date.now().toString(36)}_${rand()}${rand()}`;
}

/**
 * Compute the auto-injected event-property block. Returns only fields that
 * are actually set, never `undefined` keys.
 */
export function getEditorContextProperties(): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  const c = context;
  if (c.project_id) out.project_id = c.project_id;
  if (c.template_id) out.template_id = c.template_id;
  if (c.project_visibility) out.project_visibility = c.project_visibility;
  if (c.current_page_id) out.current_page_id = c.current_page_id;
  if (typeof c.page_count === 'number' && Number.isFinite(c.page_count)) {
    out.project_page_count = c.page_count;
  }
  const ncb = bucketCount(c.page_node_count);
  if (ncb) out.page_node_count_bucket = ncb;
  if (c.editor_session_id) out.editor_session_id = c.editor_session_id;
  if (c.editor_session_started_at) out.editor_session_started_at = c.editor_session_started_at;
  if (c.plan_tier) out.plan_tier = c.plan_tier;
  if (c.workspace_id) out.workspace_id = c.workspace_id;
  return out;
}
